import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoginForm } from "./components/LoginForm";
import { ProfileMenu } from "./components/ProfileMenu";
import { AuthModal } from "./components/AuthModal";
import { ProfilTab } from "./components/ugyfelkapu/ProfilTab";
import { FelhasznalokTab } from "./components/ugyfelkapu/FelhasznalokTab";
import { JogosultsagokTab } from "./components/ugyfelkapu/JogosultsagokTab";
import { LovakTab } from "./components/ugyfelkapu/LovakTab";
import { HianyzasTab } from "./components/ugyfelkapu/HianyzasTab";
import { KerelmekTab } from "./components/ugyfelkapu/KerelmekTab";
import { FeljegyzesekTab } from "./components/ugyfelkapu/FeljegyzesekTab";
import { KezdolapTab } from "./components/ugyfelkapu/KezdolapTab";
import { RendelesekTab } from "./components/ugyfelkapu/RendelesekTab";
import { TagsagTab } from "./components/ugyfelkapu/TagsagTab";
import { IdopontfoglalasTab } from "./components/ugyfelkapu/IdopontfoglalasTab";
import { IdopontKezelesTab } from "./components/ugyfelkapu/IdopontKezelesTab";
import { PenzugyekTab } from "./components/ugyfelkapu/PenzugyekTab";
import { BerlesTab } from "./components/ugyfelkapu/BerlesTab";
import { DokumentumokTab } from "./components/ugyfelkapu/DokumentumokTab";
import { IgenylesTab } from "./components/ugyfelkapu/IgenylesTab";
import { ChatTab } from "./components/ugyfelkapu/ChatTab";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_equestrian-hub-20/artifacts/0lyoa6pm_No%20hatter%2C%20feher.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = ["Kezdőlap", "Időpontfoglalás", "Hiányzás", "Igénylés", "Bérlés", "Dokumentumok", "Tanfolyamok", "Rendelések", "Chat", "Profil"];
const TRAINER_TABS = ["Kezdőlap", "Időpontfoglalás kezelése", "Kérelmek", "Hiányzás", "Igénylések kezelése", "Bérlések kezelése", "Dokumentumok", "Tanfolyamok", "Chat", "Lovak", "Tagság", "Feljegyzések", "Pénzügyek"];
const ADMIN_TABS = ["Kezdőlap", "Időpontfoglalás kezelése", "Kérelmek", "Hiányzás", "Igénylések kezelése", "Bérlések kezelése", "Dokumentumok", "Tanfolyamok", "Chat", "Felhasználók", "Rajtengedély / Jogosultságok / Szerepkörök", "Lovak", "Tagság", "Feljegyzések", "Pénzügyek"];
const ALL_TABS = [...new Set([...TABS, ...TRAINER_TABS, ...ADMIN_TABS])];
const tabsFor = (u) => (u?.is_admin ? ADMIN_TABS : u?.is_trainer ? TRAINER_TABS : TABS);
const TAB_KEY = "ugyfelkapu_active_tab";
const savedTab = () => { const t = localStorage.getItem(TAB_KEY); return ALL_TABS.includes(t) ? t : TABS[0]; };
const slug = (t) => t.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function Ugyfelkapu() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [modal, setModal] = useState(null); // null | "register" | "forgot"
  const [activeTab, setActiveTabState] = useState(savedTab);
  const [tabFocus, setTabFocus] = useState(null);
  const setActiveTab = (tab, focus = null) => { setActiveTabState(tab); setTabFocus(focus); localStorage.setItem(TAB_KEY, tab); };
  useEffect(() => { const t = new URLSearchParams(window.location.search).get("tab"); if (t && ALL_TABS.includes(t)) setActiveTab(t); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const tabs = tabsFor(user);
  const currentTab = tabs.includes(activeTab) ? activeTab : TABS[0];

  useEffect(() => {
    const token = localStorage.getItem("comp_token");
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/competition/me`, { params: { token } })
      .then((res) => setUser(res.data))
      .catch(() => { localStorage.removeItem("comp_token"); localStorage.removeItem("comp_user"); })
      .finally(() => setChecking(false));
  }, []);

  const handleLogout = async () => {
    const token = localStorage.getItem("comp_token");
    if (token) axios.post(`${API}/competition/logout`, null, { params: { token } }).catch(() => {});
    localStorage.removeItem("comp_token");
    localStorage.removeItem("comp_user");
    setUser(null);
    toast.success("Sikeres kijelentkezés");
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="ugyfelkapu-page">
      <header
        className="relative z-30 bg-[#F97316] rounded-b-3xl shadow-[0_8px_30px_rgba(249,115,22,0.25)]"
        data-testid="ugyfelkapu-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <img
              src={LOGO_URL}
              alt="Gepárd Lovas Egyesület"
              className="h-12 sm:h-14 w-auto object-contain shrink-0"
              data-testid="ugyfelkapu-logo"
            />
            {user && (
              <span className="text-white font-semibold text-sm sm:text-lg leading-tight truncate" data-testid="ugyfelkapu-header-title">
                Gepárd Lovas Egyesület Ügyfélkapu
              </span>
            )}
          </div>
          {user && <ProfileMenu user={user} onLogout={handleLogout} />}
        </div>
      </header>

      {user && (
        <nav className="relative z-10 bg-white border-b border-slate-200" data-testid="ugyfelkapu-tabs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
            {tabs.map((tab) => {
              const active = tab === currentTab;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={(e) => { setActiveTab(tab); e.currentTarget.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" }); }}
                  aria-selected={active}
                  role="tab"
                  className={`relative shrink-0 whitespace-nowrap px-3 sm:px-4 py-3.5 text-sm font-medium transition-colors ${active ? "text-[#EA6C0A]" : "text-slate-700 hover:text-[#EA6C0A]"}`}
                  data-testid={`tab-${slug(tab)}`}
                >
                  {tab}
                  <span className={`absolute left-2 right-2 bottom-0 h-[3px] rounded-full bg-[#F97316] transition-opacity ${active ? "opacity-100" : "opacity-0"}`} />
                </button>
              );
            })}
          </div>
        </nav>
      )}

      <main className="flex-1 px-4 sm:px-6 pt-8 sm:pt-10 pb-16" data-testid="ugyfelkapu-content">
        {user && (
          <section className="max-w-7xl mx-auto" data-testid={`tab-panel-${slug(currentTab)}`}>
            {currentTab === "Profil" ? (
              <ProfilTab onUserUpdate={(p) => { const u = { ...user, ...p }; setUser(u); localStorage.setItem("comp_user", JSON.stringify(u)); }} />
            ) : currentTab === "Felhasználók" ? (
              <FelhasznalokTab />
            ) : currentTab === "Rajtengedély / Jogosultságok / Szerepkörök" ? (
              <JogosultsagokTab />
            ) : currentTab === "Lovak" && (user.is_admin || user.is_trainer) ? (
              <LovakTab />
            ) : currentTab === "Tagság" && (user.is_admin || user.is_trainer) ? (
              <TagsagTab readOnly={!user.is_admin} />
            ) : currentTab === "Időpontfoglalás" && !user.is_admin ? (
              <IdopontfoglalasTab user={user} />
            ) : currentTab === "Időpontfoglalás kezelése" && (user.is_admin || user.is_trainer) ? (
              <IdopontKezelesTab user={user} />
            ) : currentTab === "Pénzügyek" && (user.is_admin || user.is_trainer) ? (
              <PenzugyekTab readOnly={!user.is_admin} />
            ) : currentTab === "Bérlés" && !user.is_admin ? (
              <BerlesTab />
            ) : currentTab === "Bérlések kezelése" && (user.is_admin || user.is_trainer) ? (
              <BerlesTab manage />
            ) : currentTab === "Dokumentumok" ? (
              <DokumentumokTab manage={!!(user.is_admin || user.is_trainer)} />
            ) : currentTab === "Igénylés" && !(user.is_admin || user.is_trainer) ? (
              <IgenylesTab />
            ) : currentTab === "Igénylések kezelése" && (user.is_admin || user.is_trainer) ? (
              <IgenylesTab manage />
            ) : currentTab === "Chat" ? (
              <ChatTab user={user} />
            ) : currentTab === "Hiányzás" ? (
              <HianyzasTab user={user} />
            ) : currentTab === "Kérelmek" && (user.is_admin || user.is_trainer) ? (
              <KerelmekTab focus={tabFocus} isAdmin={!!user.is_admin} />
            ) : currentTab === "Feljegyzések" && (user.is_admin || user.is_trainer) ? (
              <FeljegyzesekTab />
            ) : currentTab === "Rendelések" ? (
              <RendelesekTab />
            ) : currentTab === "Kezdőlap" ? (
              <KezdolapTab user={user} onNavigate={(tab, focus) => setActiveTab(tab, focus)} />
            ) : (
              <p className="text-center text-slate-700 text-lg sm:text-xl font-medium py-16" data-testid="tab-coming-soon">Hamarosan</p>
            )}
          </section>
        )}
        {!checking && !user && (
          <section className="mx-auto w-full max-w-md" data-testid="ugyfelkapu-login-section">
            <h1 className="mx-auto -mx-2 sm:-mx-16 text-center text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-8" data-testid="ugyfelkapu-title">
              Gepárd Lovas Egyesület Ügyfélkapu
            </h1>
            <div className="rounded-3xl bg-white p-6 sm:p-8 shadow-[0_10px_40px_rgba(15,23,42,0.08)] ring-1 ring-slate-100" data-testid="ugyfelkapu-login-card">
              <LoginForm
                testIdPrefix="ugyfelkapu-login"
                onSuccess={(u) => setUser(u)}
                onRegister={() => setModal("register")}
                onForgot={() => setModal("forgot")}
              />
            </div>
          </section>
        )}
      </main>

      {modal && (
        <AuthModal
          key={modal}
          show
          initialMode={modal === "register" ? "register" : "login"}
          initialStep={modal === "forgot" ? "forgot" : "auth"}
          onClose={() => setModal(null)}
          onLoginSuccess={(u) => { setUser(u); setModal(null); }}
        />
      )}
    </div>
  );
}
